import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { saveReviewField } from '../utils/applicationReviews';

const NAVY      = '#0D1B2A';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const GREEN     = '#1D9E75';
const RED       = '#DC2626';
const ERROR_TEXT = '#F09595';
const FONT_BODY = "'DM Sans', sans-serif";

const EXAM_OPTIONS = [
  { value: null,     labelKey: 'union.dashboard.candidates.review.examNotSet', fill: NAVY },
  { value: 'passed', labelKey: 'union.dashboard.candidates.review.examPassed', fill: GREEN },
  { value: 'failed', labelKey: 'union.dashboard.candidates.review.examFailed', fill: RED },
];

const labelStyle = { fontSize: '0.66rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' };

// Staff-only exam result / requirements met / notes for one applicant, on the union's Candidates
// tab. Each control writes only its own field (saveReviewField); the display is optimistic, but a
// write that doesn't come back persisted reverts the control to the last saved value and shows an
// error. `review` is the persisted state from the parent, which onPersisted keeps current -- so
// the summary chips and the Compare view only ever reflect confirmed values.
//
// Renders three sibling blocks (exam, requirements, notes) so the parent decides the layout: the
// desktop row places them as columns, the expanded mobile card stacks them.
export default function ApplicantReviewControls({ supabase, applicationId, review, total, onPersisted, isMobile, blockStyles = {} }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState({}); // field -> optimistic value awaiting its write
  const [notesDraft, setNotesDraft] = useState(review.staff_notes || '');
  const [notesFocused, setNotesFocused] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const inFlight = useRef(0);
  const seq = useRef({});      // field -> sequence number of its newest write
  const confirmed = useRef({}); // field -> sequence number of its newest persisted write
  const reviewRef = useRef(review);
  reviewRef.current = review;

  // Pick up values loaded or saved elsewhere, but never yank text out from under the cursor.
  useEffect(() => {
    if (!notesFocused && !('staff_notes' in pending)) setNotesDraft(review.staff_notes || '');
  }, [review.staff_notes]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = field => (field in pending ? pending[field] : review[field]);

  const save = async (field, value) => {
    if ((value ?? null) === (shown(field) ?? null) && !(field in pending)) return;
    const mine = (seq.current[field] || 0) + 1;
    seq.current[field] = mine;
    setPending(p => ({ ...p, [field]: value }));
    inFlight.current += 1;
    setSaveState('saving');

    const res = await saveReviewField(supabase, applicationId, field, value);
    inFlight.current -= 1;
    const isLatest = seq.current[field] === mine;
    if (res.ok && mine > (confirmed.current[field] || 0)) {
      confirmed.current[field] = mine;
      onPersisted(applicationId, field, res.review[field]);
    }
    if (!isLatest) return; // a newer write to this field owns the control and the status line
    setPending(p => { const { [field]: _, ...rest } = p; return rest; });
    if (res.ok) {
      setSaveState(s => (s === 'error' ? s : inFlight.current > 0 ? 'saving' : 'saved'));
    } else {
      // Revert to the last persisted value. For notes that's the draft text too.
      if (field === 'staff_notes') setNotesDraft(reviewRef.current.staff_notes || '');
      setSaveState('error');
    }
  };

  const exam = shown('exam_result') ?? null;
  const met = shown('requirements_met');
  const touch = isMobile ? 48 : 34;

  const stepBtn = disabled => ({
    width: `${touch}px`, height: `${touch}px`, flexShrink: 0, borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.2)', background: 'transparent',
    color: disabled ? 'rgba(255,255,255,0.25)' : WHITE, fontSize: '18px', fontWeight: 700,
    cursor: disabled ? 'default' : 'pointer', fontFamily: FONT_BODY, lineHeight: 1,
  });
  const metValue = met ?? null;
  // Nothing recorded yet counts as 0, so the first + records 1 and the first − records 0.
  const step = delta => save('requirements_met', Math.min(total, Math.max(0, (metValue ?? 0) + delta)));

  return (
    <>
      <div data-review="exam" style={blockStyles.exam}>
        <p style={labelStyle}>{t('compare.union.rows.examResult')}</p>
        <div
          role="radiogroup"
          aria-label={t('compare.union.rows.examResult')}
          style={{ display: 'flex', width: isMobile ? '100%' : 'auto', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.25)', background: WHITE }}
        >
          {EXAM_OPTIONS.map((opt, i) => {
            const selected = exam === opt.value;
            return (
              <button
                key={opt.labelKey}
                type="button"
                role="radio"
                aria-checked={selected}
                data-exam-option={opt.value || 'none'}
                onClick={() => save('exam_result', opt.value)}
                style={{
                  flex: isMobile ? 1 : '0 0 auto', minHeight: `${touch}px`, padding: '0 14px',
                  border: 'none', borderLeft: i ? '1px solid rgba(13,27,42,0.15)' : 'none',
                  background: selected ? opt.fill : WHITE, color: selected ? WHITE : NAVY,
                  fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: FONT_BODY, whiteSpace: 'nowrap',
                }}
              >
                {t(opt.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      {total > 0 && (
        <div data-review="requirements" style={blockStyles.requirements}>
          <p style={labelStyle}>{t('compare.union.rows.requirementsMet')}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button type="button" aria-label={t('union.dashboard.candidates.review.decrease')} disabled={metValue !== null && metValue <= 0} onClick={() => step(-1)} style={stepBtn(metValue !== null && metValue <= 0)}>−</button>
            <span data-review="met-value" style={{ minWidth: '64px', textAlign: 'center', fontSize: '0.88rem', fontWeight: 700, color: WHITE, whiteSpace: 'nowrap' }}>
              {t('union.dashboard.candidates.review.metOfTotal', { met: metValue ?? t('union.dashboard.candidates.review.metUnset'), total })}
            </span>
            <button type="button" aria-label={t('union.dashboard.candidates.review.increase')} disabled={metValue !== null && metValue >= total} onClick={() => step(1)} style={stepBtn(metValue !== null && metValue >= total)}>+</button>
          </div>
        </div>
      )}

      <div data-review="notes" style={blockStyles.notes}>
        <p style={labelStyle}>
          {t('compare.union.rows.staffNotes')}
          <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500, marginLeft: '6px', color: 'rgba(255,255,255,0.3)' }}>· {t('union.dashboard.candidates.review.staffOnly')}</span>
        </p>
        <textarea
          value={notesDraft}
          placeholder={t('union.dashboard.candidates.review.notesPlaceholder')}
          onFocus={() => setNotesFocused(true)}
          onChange={e => setNotesDraft(e.target.value)}
          onBlur={() => { setNotesFocused(false); save('staff_notes', notesDraft.trim() ? notesDraft : null); }}
          rows={isMobile ? 4 : 2}
          style={{
            width: '100%', boxSizing: 'border-box', minHeight: isMobile ? '96px' : '56px', resize: 'vertical',
            background: NAVY, border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '10px 12px',
            color: WHITE, fontSize: isMobile ? '16px' : '0.82rem', fontFamily: FONT_BODY, lineHeight: 1.45, outline: 'none',
          }}
        />
        <p
          data-review="save-state"
          data-state={saveState}
          role={saveState === 'error' ? 'alert' : 'status'}
          aria-live="polite"
          style={{ minHeight: '18px', marginTop: '4px', fontSize: '0.74rem', fontWeight: saveState === 'error' ? 700 : 500,
            color: saveState === 'error' ? ERROR_TEXT : saveState === 'saved' ? GREEN : saveState === 'saving' ? AMBER : 'transparent' }}
        >
          {saveState === 'saving' && t('union.dashboard.candidates.review.saving')}
          {saveState === 'saved' && t('union.dashboard.candidates.review.saved')}
          {saveState === 'error' && t('union.dashboard.candidates.review.saveError')}
        </p>
      </div>
    </>
  );
}
